import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DoctorVisitDetails } from '../coordinator/entities/doctor-visit';
import { SharedService } from 'app/modules/common/services/shared-service';
import { DespachoService } from './services/despacho.service';
import { FileExportService } from 'app/modules/selection-generic/services/file-export.service';
import {
    DespachoColaboradoresReportDialogResult,
    DespachoColaboradoresReportPopupComponent
} from './popups/despacho-colaboradores-report-popup/despacho-colaboradores-report-popup.component';

interface VisitaResumen {
    id: number;
    fecha: string;
    fechaCompleta: string;
    realizada: boolean;
    mes: number;
}

interface Medico {
    id: number;
    medicoId: number;
    nombre: string;
    visitas: { [key: string]: VisitaResumen[] };
    visitasOrdenadas: VisitaResumen[];
}

export interface Coordinador {
    id: number;
    nombre: string;
}

@Component({
    selector: 'app-despacho-colaboradores',
    templateUrl: './despacho-colaboradores.component.html',
    styleUrls: ['./despacho-colaboradores.component.scss'],
    standalone: false,
})
export class DespachoColaboradoresComponent implements OnInit {
    @Input() apiUrl: string = '../common/services/api.config.json';
    @Output() onError = new EventEmitter<any>();
    @Output() onAction = new EventEmitter<{ action: string, data: any }>();

    year: number = new Date().getFullYear();
    coordinadorSeleccionado: number | null = null;
    coordinadorNombre: string = '';
    months: string[] = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    medicos: Medico[] = [];
    coordinadores: Coordinador[] = [];

    loading: boolean = false;
    reportLoading: boolean = false;
    error: string | null = null;
    medicoSeleccionado: number | null = null;
    visitaSeleccionadaId: number | null = null;

    fechaReunion: string = '';
    coordinadorDetalle: string = '';
    asistentesDetalle: string = '';
    comentariosDetalle: string = '';

    constructor(
        private dialog: MatDialog,
        private despachoService: DespachoService,
        private sharedService: SharedService,
        private fileExportService: FileExportService,
    ) { }

    ngOnInit(): void {
        this.cargarCoordinadores();
    }

    cargarCoordinadores(): void {
        this.loading = true;
        this.error = null;

        this.despachoService.GetAllCoordinatorsList().subscribe(res => {
            if (res && Array.isArray(res.result)) {
                this.coordinadores = this.normalizarCoordinadores(res.result);

                if (this.coordinadores.length > 0) {
                    this.seleccionarCoordinador(this.coordinadores[0].id);
                    return;
                }

                this.medicos = [];
            } else {
                this.error = 'No se pudieron cargar los coordinadores';
            }

            this.loading = false;
        }, err => {
            console.error(err);
            this.error = 'Error al cargar coordinadores';
            this.loading = false;
            this.onError.emit(err);
        });
    }

    cambiarCoordinador(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.seleccionarCoordinador(value);
    }

    cambiarYear(incremento: number): void {
        this.year += incremento;

        if (this.coordinadorSeleccionado) {
            this.GetDoctorList();
        }
    }

    seleccionarMedico(medico: Medico): void {
        this.medicoSeleccionado = medico.medicoId;

        if (medico.visitasOrdenadas.length === 0) {
            this.clearSelectedVisit();
            return;
        }

        const currentVisitBelongsToDoctor = medico.visitasOrdenadas.some(visit => visit.id === this.visitaSeleccionadaId);
        if (!currentVisitBelongsToDoctor) {
            this.seleccionarVisita(medico, medico.visitasOrdenadas[0]);
        }
    }

    seleccionarVisita(medico: Medico, visita: VisitaResumen, event?: MouseEvent): void {
        event?.stopPropagation();

        this.medicoSeleccionado = medico.medicoId;
        this.visitaSeleccionadaId = visita.id;
        this.fechaReunion = visita.fechaCompleta;
        this.coordinadorDetalle = this.coordinadorNombre;
        this.asistentesDetalle = '';
        this.comentariosDetalle = '';

        this.despachoService.GetDoctorVisitDetails(visita.id.toString()).subscribe(res => {
            if (res && res.code === 0 && res.result) {
                this.aplicarDetalleVisita(res.result);
            } else {
                this.clearSelectedVisit();
                this.error = 'No se pudieron cargar los detalles de la visita';
            }
        }, err => {
            console.error(err);
            this.clearSelectedVisit();
            this.error = 'Error al cargar los detalles de la visita';
            this.onError.emit(err);
        });
    }

    public GetDoctorList(): void {
        if (!this.coordinadorNombre) {
            this.medicos = [];
            this.loading = false;
            this.sharedService.ShowSnackBar('Por favor seleccione un coordinador');
            return;
        }

        this.loading = true;
        this.error = null;
        this.medicoSeleccionado = null;
        this.clearSelectedVisit();

        this.despachoService.GetDoctorListCoordinator(
            this.coordinadorNombre,
            this.year.toString()
        ).subscribe(res => {
            if (res && res.code === 0) {
                this.medicos = Array.isArray(res.result) ? this.procesarDatosMedicos(res.result) : [];
            } else {
                this.medicos = [];
                this.sharedService.ShowSnackBar('No se pudo cargar a los medicos del coordinador.');
            }

            this.loading = false;
        }, err => {
            console.error(err);
            this.medicos = [];
            this.loading = false;
            this.sharedService.ShowSnackBar('Se produjo un error, actualice la pagina.');
            this.onError.emit(err);
        });
    }

    crear(): void {
        this.onAction.emit({
            action: 'crear',
            data: {
                coordinadorId: this.coordinadorSeleccionado,
                coordinador: this.coordinadorNombre,
                medicoId: this.medicoSeleccionado
            }
        });
    }

    eliminar(): void {
        if (!this.visitaSeleccionadaId) {
            this.error = 'Debe seleccionar una visita para eliminar';
            return;
        }

        this.onAction.emit({
            action: 'eliminar',
            data: { id: this.visitaSeleccionadaId }
        });
    }

    editar(): void {
        if (!this.visitaSeleccionadaId) {
            this.error = 'Debe seleccionar una visita para editar';
            return;
        }

        this.onAction.emit({
            action: 'editar',
            data: { id: this.visitaSeleccionadaId }
        });
    }

    guardar(): void {
        this.onAction.emit({ action: 'guardar', data: this.medicos });
    }

    deshacer(): void {
        this.GetDoctorList();
    }

    generarInformeWord(): void {
        if (!this.visitaSeleccionadaId) {
            this.sharedService.ShowSnackBar('Debe seleccionar una visita para generar el Word');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetDoctorVisitWordReport(this.visitaSeleccionadaId.toString()).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
            this.onError.emit(err);
        });
    }

    abrirInformeRango(): void {
        if (!this.coordinadorNombre) {
            this.sharedService.ShowSnackBar('Debe seleccionar un coordinador para generar el informe');
            return;
        }

        const selectedMedico = this.getMedicoSeleccionado();
        const dialogRef = this.dialog.open(DespachoColaboradoresReportPopupComponent, {
            width: '460px',
            autoFocus: false,
            data: {
                coordinador: this.coordinadorNombre,
                year: this.year,
                medicoId: this.medicoSeleccionado,
                medicoNombre: selectedMedico ? selectedMedico.nombre : ''
            }
        });

        dialogRef.afterClosed().subscribe((result: DespachoColaboradoresReportDialogResult | undefined) => {
            if (!result) {
                return;
            }

            this.generarInformeWordRango(result);
        });
    }

    generarInformeWordRango(result: DespachoColaboradoresReportDialogResult): void {
        if (result.scope === 'medico' && !this.medicoSeleccionado) {
            this.sharedService.ShowSnackBar('Debe seleccionar un medico para generar el informe');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetDoctorVisitWordReportByRange({
            coordinador: this.coordinadorNombre,
            medicoId: result.scope === 'medico' ? this.medicoSeleccionado : null,
            dateFrom: result.dateFrom,
            dateTo: result.dateTo,
            scope: result.scope
        }).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No hay datos para generar el informe');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe');
            this.onError.emit(err);
        });
    }

    getMedicoSeleccionado(): Medico | undefined {
        if (!this.medicoSeleccionado) {
            return undefined;
        }

        return this.medicos.find(medico => medico.medicoId === this.medicoSeleccionado);
    }

    private seleccionarCoordinador(id: number | string): void {
        const coord = this.coordinadores.find(c => c.id == id);

        if (!coord) {
            this.coordinadorSeleccionado = null;
            this.coordinadorNombre = '';
            this.medicos = [];
            this.clearSelectedVisit();
            return;
        }

        this.coordinadorSeleccionado = coord.id;
        this.coordinadorNombre = coord.nombre;
        this.GetDoctorList();
    }

    private normalizarCoordinadores(coordinadores: any[]): Coordinador[] {
        return coordinadores
            .map(c => ({
                id: c.id ?? c.Id ?? c.coordinadorId ?? c.CoordinadorId,
                nombre: c.nombre ?? c.Nombre ?? c.name ?? c.Name
            }))
            .filter(c => c.id !== undefined && c.id !== null && !!c.nombre);
    }

    private procesarDatosMedicos(datos: any[]): Medico[] {
        const medicosPorId: { [key: number]: Medico } = {};

        datos.forEach(item => {
            const medicoId = Number(item.medicoId ?? item.MedicoId);

            if (!medicoId) {
                return;
            }

            if (!medicosPorId[medicoId]) {
                medicosPorId[medicoId] = {
                    id: medicoId,
                    medicoId,
                    nombre: item.nombre ?? item.Nombre ?? 'Sin nombre',
                    visitas: {},
                    visitasOrdenadas: []
                };
            }

            const mes = Number(item.mes ?? item.Mes);
            if (!mes || mes < 1 || mes > this.months.length) {
                return;
            }

            const visitaId = Number(item.id ?? item.Id);
            const realizada = (item.estaRealizada ?? item.EstaRealizada) === 1;
            const fecha = this.buildFechaResumen(item.fechaVisita ?? item.FechaVisita, realizada);
            const visita: VisitaResumen = {
                id: visitaId,
                fecha,
                fechaCompleta: this.buildFechaCompleta(item.fechaVisita ?? item.FechaVisita),
                realizada,
                mes
            };

            const monthKey = this.months[mes - 1];
            if (!medicosPorId[medicoId].visitas[monthKey]) {
                medicosPorId[medicoId].visitas[monthKey] = [];
            }

            medicosPorId[medicoId].visitas[monthKey].push(visita);
            medicosPorId[medicoId].visitasOrdenadas.push(visita);
        });

        const medicos = Object.values(medicosPorId);

        medicos.forEach(medico => {
            medico.visitasOrdenadas.sort((a, b) => this.getVisitaSortValue(b) - this.getVisitaSortValue(a));

            this.months.forEach(month => {
                if (medico.visitas[month]) {
                    medico.visitas[month].sort((a, b) => this.getVisitaSortValue(a) - this.getVisitaSortValue(b));
                }
            });
        });

        return medicos;
    }

    private aplicarDetalleVisita(detalle: DoctorVisitDetails): void {
        this.fechaReunion = this.formatDate(detalle.realizada);
        this.coordinadorDetalle = detalle.coordinador ?? '';
        this.asistentesDetalle = detalle.asistentes ?? '';
        this.comentariosDetalle = detalle.comentarios ?? '';
        this.error = null;
    }

    private clearSelectedVisit(): void {
        this.visitaSeleccionadaId = null;
        this.fechaReunion = '';
        this.coordinadorDetalle = '';
        this.asistentesDetalle = '';
        this.comentariosDetalle = '';
    }

    private buildFechaResumen(value: string, realizada: boolean): string {
        const fecha = value ? String(value) : '';
        if (fecha) {
            return fecha;
        }

        return realizada ? '' : '**';
    }

    private buildFechaCompleta(value: string): string {
        const fecha = value ? String(value) : '';
        if (!fecha) {
            return '';
        }

        if (fecha.split('/').length === 2) {
            return `${fecha}/${this.year}`;
        }

        return fecha;
    }

    private getVisitaSortValue(visita: VisitaResumen): number {
        const parts = visita.fechaCompleta.split('/');
        if (parts.length !== 3) {
            return visita.mes * 100;
        }

        const day = Number(parts[0]);
        const month = Number(parts[1]);
        const year = Number(parts[2]);

        if ([day, month, year].some(value => Number.isNaN(value))) {
            return visita.mes * 100;
        }

        return new Date(year, month - 1, day).getTime();
    }

    private formatDate(value: string): string {
        if (!value) {
            return '';
        }

        const normalizedValue = String(value);
        const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }
}
